/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import { graphql } from 'react-apollo';
import { compose, mapProps } from 'recompose';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';

import ThreeppPopover from 'components/common/Popover/Popover';
import UserBadge from 'components/threepp/UserBadge/UserBadge';
import { showInfoMessage } from 'components/common/Alert/Alert';
import { getIdFromUrlParams } from 'selectors/assessment/selector';
import { getCurrentUser as getCurrentUserSelector } from 'selectors/user/selector';
import {
  listFieldsState,
  submitAssessment,
  getCurrentUser,
  getActiveUsers,
  userOpenAssessment as userOpenAssessmentMut,
  userCloseAssessment as userCloseAssessmentMut
} from 'graphql/operations';
import { NETWORK_ONLY } from 'graphql/fetchPolicies';
import { sortUsersByCurrentFirst, uuid, getObjValue } from 'utils/functions';

import { getFormattedName, FIRST_LAST } from 'selectors/formatName/selector';

import './ActiveUsers.scss';

export const ActiveUsers = props => {
  const { userOpenAssessment, userCloseAssessment, activeUsers, currentUser, activeUsersData } = props;

  const { t } = useTranslation();

  const [showActiveUserList, setShowActiveUserList] = useState(false);
  const [activeUsersWithCurrent, setActiveUsersWithCurrent] = useState([]);
  const [prevActiveUsers, setPrevActiveUsers] = useState(activeUsers);

  const popoverTargetId = useRef(`PopoverTargetId-${uuid()}`);

  // detects changes on the active-users list and triggers notifications.
  useEffect(() => {
    // if there are new users, show a notification per user.
    if (activeUsers.length > prevActiveUsers.length) {
      const newUsers = _.difference(activeUsers, prevActiveUsers);

      newUsers.forEach(user => {
        if (props.currentUser.email !== user.email) {
          showInfoMessage(t('assessment.simultaneousEdit.hasJoin', { name: getFormattedName({ user }) }), {
            autoHideDuration: 3000
          });
        }
      });
    }

    setPrevActiveUsers(activeUsers);
  }, [activeUsers.length]);

  // invokes OPEN and CLOSE when accessing and leaving the assessment
  useEffect(() => {
    const assessmentId = activeUsersData?.id;

    userOpenAssessment({ variables: { assessmentId } }).catch(error => {
      console.error('userOpenAssessment OPEN mutation error', error);
    });

    return () => {
      userCloseAssessment({ variables: { assessmentId } }).catch(error => {
        console.error('userCloseAssessment CLOSE mutation error', error);
      });
    };
  }, [activeUsersData?.id]);

  // Keeps track of active-user list on the state,
  // adds current user if not present
  // sorts the list
  useEffect(() => {
    const updatedList = activeUsers ? [...activeUsers] : [];
    if (updatedList.findIndex(u => u.email === currentUser.email) === -1) {
      updatedList.unshift(currentUser);
    }
    updatedList.sort(sortUsersByCurrentFirst(currentUser));
    setActiveUsersWithCurrent(updatedList);
    if (updatedList.length < 2) {
      setShowActiveUserList(false);
    }
  }, [activeUsers, currentUser]);

  return (
    <span className="active-users">
      {activeUsersWithCurrent.length > 1 && (
        <ThreeppPopover
          toggle={() => setShowActiveUserList(!showActiveUserList)}
          isOpen={showActiveUserList}
          className="active-users-popover"
          target={popoverTargetId.current}
          placement="auto"
          trigger="legacy"
        >
          <span className="active-user-list">
            {activeUsersWithCurrent.map(user => (
              <UserBadge key={user.email} user={user} format={FIRST_LAST} />
            ))}
          </span>
        </ThreeppPopover>
      )}

      <span className={`active-users-label ${activeUsersWithCurrent.length > 1 ? ' visible' : 'hidden'}`}>
        {t('assessment.simultaneousEdit.youAreOneOf')}{' '}
        <span id={popoverTargetId.current} className="number-of-active-users">
          {t('assessment.simultaneousEdit.nParticipants', { count: activeUsersWithCurrent.length })}
        </span>{' '}
        {t('assessment.simultaneousEdit.activeUsersWorking')}
      </span>
    </span>
  );
};

export const ActiveUsersHOC = compose(
  withRouter,
  graphql(listFieldsState, { name: 'listFieldsState' }),
  graphql(getCurrentUser, { name: 'getCurrentUser' }),
  graphql(userOpenAssessmentMut, { name: 'userOpenAssessment' }),
  graphql(userCloseAssessmentMut, { name: 'userCloseAssessment' }),
  graphql(submitAssessment, {
    options: props => ({
      variables: {
        assessmentId: getIdFromUrlParams(props)
      }
    }),
    name: 'submitAssessment'
  }),
  graphql(getActiveUsers, {
    options: props => ({
      variables: {
        id: getIdFromUrlParams(props)
      },
      fetchPolicy: NETWORK_ONLY
    }),
    name: 'getActiveUsers'
  }),
  mapProps(props => ({
    activeUsersData: props.getActiveUsers.getActiveUsers,
    currentUser: getCurrentUserSelector(props),
    userOpenAssessment: props.userOpenAssessment,
    userCloseAssessment: props.userCloseAssessment,
    activeUsers: getObjValue(props, 'getActiveUsers.getActiveUsers.activeUsers', [])
  }))
);

export default ActiveUsersHOC(ActiveUsers);
